/**
 * The treasure among the foods (spec 02 §7.3): the round-cut gems, the brilliant-cut diamonds
 * (also the giant bonus diamonds, 02 §7.6, drawn 2.6x), the crowns geon calls "queens", the
 * crystal "shard" and the pendant necklaces ("lockets"). Gems are cut with flat facets on
 * purpose; the metal round them is smooth. Shapes follow the C64's own item graphics.
 */

import { Matrix4, Vector3 } from 'three';
import { type MeshBuilder, place } from '../../kit/MeshBuilder';
import type { Palette } from '../../palette';
import { type Builder, chain, faceCamera, lathe, rotZ, scaled, torus } from './kit';

const TAU = Math.PI * 2;

/**
 * A brilliant-cut diamond standing on its point, as the item icon draws it: a deep pavilion, a
 * bright girdle, triangular star facets on the crown and a flat table. `ramp` has four steps.
 */
function diamond(mb: MeshBuilder, p: Palette, ramp: string): void {
  const c = (i: number): ReturnType<Palette['color']> => p.color(`${ramp}.${i}`);
  const sides = 8;
  const half = Math.PI / sides;
  lathe(mb, [[0, 0], [0.54, 0.6], [0.54, 0.66], [0.36, 0.9], [0, 0.9]], (seg, k) => {
    if (seg === 0) return c(k % 2 ? 1 : 2);
    if (seg === 1) return c(3);
    if (seg === 2) return c(k % 2 ? 1 : 2);
    return c(2);
  }, undefined, { sides, flat: true, twist: [0, 0, 0, half, half] });
  // A glint on the upper left, where the key light strikes.
  mb.blob(0.07, 0.07, 0.04, p.color('paper.0'), place(-0.2, 0.8, 0.28), { detail: 1 });
}

/** A round-cut stone in a gold bezel, standing on edge and facing the camera. */
function gem(mb: MeshBuilder, p: Palette, ramp: string): void {
  const c = (i: number): ReturnType<Palette['color']> => p.color(`${ramp}.${i}`);
  const sides = 8;
  const half = Math.PI / sides;
  const cy = 0.5;
  const m = chain(new Matrix4().makeTranslation(0, cy, 0), faceCamera());
  lathe(mb, [[0, -0.2], [0.42, -0.02], [0.42, 0.06], [0.27, 0.2], [0, 0.2]], (seg, k) => {
    if (seg === 0) return c(0);
    if (seg === 1) return c(1);
    if (seg === 2) return c(k % 2 ? 0 : 1);
    return c(1);
  }, m, { sides, flat: true, phase: half, twist: [0, 0, 0, half, half] });
  torus(mb, 0.44, 0.05, p.color('gold.1'), place(0, cy, 0.02), 32, 8);
  // Four claws holding the stone.
  for (let i = 0; i < 4; i++) {
    const a = (i / 4) * TAU + Math.PI / 4;
    mb.blob(0.05, 0.05, 0.05, p.color('gold.2'), place(Math.cos(a) * 0.4, cy + Math.sin(a) * 0.4, 0.1), { detail: 1 });
  }
  mb.blob(0.06, 0.06, 0.03, p.color('paper.0'), place(-0.12, cy + 0.16, 0.22), { detail: 1 });
}

/**
 * A crown: a band set with stones, five points tipped with pearls round a velvet cap, and an
 * orb and cross on top. `velvet` is the queen's colour; the yellow queen's cap is gold itself.
 */
function crown(mb: MeshBuilder, p: Palette, velvet: string, jewel: string): void {
  const gold = (i: number): ReturnType<Palette['color']> => p.color(`gold.${i}`);
  const R = 0.46;
  // The velvet cap inside, domed above the points.
  mb.blob(0.4, 0.36, 0.38, p.color(`${velvet}.1`), place(0, 0.36, 0), { detail: 3, topColor: p.color(`${velvet}.2`) });
  // The band, a little flared at top and bottom.
  lathe(mb, [[0, 0], [R - 0.03, 0], [R, 0.03], [R - 0.02, 0.14], [R, 0.25], [R - 0.04, 0.28], [0, 0.28]],
    (seg) => (seg === 3 || seg === 4 ? gold(2) : gold(1)), undefined, { sides: 28 });
  // Eight points round the rim, one zigzag wall seen from outside and in, each tipped with a pearl.
  const points = 8;
  const rim = (k: number): number => (k % 2 ? 0.4 : 0.66);
  for (const [r, out] of [[R - 0.02, true], [R - 0.06, false]] as const) {
    for (let k = 0; k < points * 2; k++) {
      const a0 = (k / (points * 2)) * TAU + TAU / (points * 4);
      const a1 = ((k + 1) / (points * 2)) * TAU + TAU / (points * 4);
      const v = (a: number, y: number): Vector3 => new Vector3(Math.sin(a) * r, y, Math.cos(a) * r);
      const n = (a: number): Vector3 => new Vector3(Math.sin(a), 0, Math.cos(a)).multiplyScalar(out ? 1 : -1);
      const [A, B, C, D] = [v(a0, 0.24), v(a1, 0.24), v(a1, rim(k + 1)), v(a0, rim(k))];
      const c = out ? gold(1) : gold(0);
      if (out) {
        mb.triSmooth(A, B, C, n(a0), n(a1), n(a1), c);
        mb.triSmooth(A, C, D, n(a0), n(a1), n(a0), c);
      } else {
        mb.triSmooth(A, C, B, n(a0), n(a1), n(a1), c);
        mb.triSmooth(A, D, C, n(a0), n(a0), n(a1), c);
      }
    }
  }
  for (let k = 0; k < points; k++) {
    const a = (k / points) * TAU + TAU / (points * 4);
    mb.blob(0.065, 0.065, 0.065, p.color('paper.0'), place(Math.sin(a) * (R - 0.04), 0.7, Math.cos(a) * (R - 0.04)),
      { detail: 2 });
    // A stone set in the band below every other point.
    if (k % 2) continue;
    const s = chain(new Matrix4().makeRotationY(a), new Matrix4().makeTranslation(0, 0.14, R + 0.01));
    mb.blob(0.075, 0.065, 0.03, p.color(`${jewel}.1`), s, { detail: 1, topColor: p.color(`${jewel}.2`) });
  }
  // The orb and cross.
  mb.blob(0.08, 0.08, 0.08, gold(1), place(0, 0.74, 0), { detail: 2, topColor: gold(2) });
  mb.box(0.05, 0.2, 0.05, gold(2), place(0, 0.78, 0));
  mb.box(0.15, 0.05, 0.05, gold(2), place(0, 0.89, 0));
}

/** A hexagonal crystal with a pointed tip, `h` tall to the shoulder. */
function crystal(mb: MeshBuilder, p: Palette, m: Matrix4, r: number, h: number): void {
  const c = (i: number): ReturnType<Palette['color']> => p.color(`citrine.${i}`);
  lathe(mb, [[0, 0], [r, 0], [r, h], [0, h + r * 1.5]], (seg, k) => (seg === 1 ? c(k % 2 ? 1 : 2) : c(k % 2 ? 2 : 3)),
    m, { sides: 6, flat: true, phase: Math.PI / 6 });
}

/**
 * A necklace: a loop of gold beads with a pendant hanging at the bottom, a cut stone in a frame.
 * `pearl` frames the red locket's stone in pearls instead of gold, so the two differ at a glance.
 */
function locket(mb: MeshBuilder, p: Palette, stone: string, pearl: boolean): void {
  const cy = 0.7;
  const beads = 18;
  for (let i = 0; i < beads; i++) {
    const a = -Math.PI / 2 + ((i + 0.5) / beads) * TAU;
    if (Math.abs(Math.sin(a) + 1) < 0.08) continue;
    const x = Math.cos(a) * 0.38;
    const y = cy + Math.sin(a) * 0.3;
    mb.blob(0.055, 0.055, 0.055, p.color(i % 2 ? 'gold.1' : 'gold.2'), place(x, y, 0), { detail: 1 });
  }
  // The bail joining the pendant to the chain.
  torus(mb, 0.06, 0.025, p.color('gold.1'), place(0, 0.5, 0).multiply(new Matrix4().makeRotationY(Math.PI / 2)), 12, 6);
  const oval = pearl ? 1 : 1.2;
  const at = new Matrix4().makeTranslation(0, 0.28 * oval, 0);
  if (pearl) {
    for (let i = 0; i < 12; i++) {
      const a = (i / 12) * TAU;
      mb.blob(0.055, 0.055, 0.055, p.color('pearl.1'), chain(at, place(Math.cos(a) * 0.21, Math.sin(a) * 0.21, 0)), {
        detail: 2, topColor: p.color('pearl.2'),
      });
    }
  } else {
    torus(mb, 0.2, 0.045, p.color('gold.1'), chain(at, scaled(0, 0, 0, 1, oval, 1)), 28, 8);
  }
  const half = Math.PI / 8;
  lathe(mb, [[0, -0.08], [0.18, 0], [0.18, 0.03], [0.11, 0.1], [0, 0.1]], (seg, k) => {
    if (seg === 0) return p.color(`${stone}.1`);
    if (seg === 2) return p.color(`${stone}.${k % 2 ? 2 : 3}`);
    return p.color(`${stone}.2`);
  }, chain(at, scaled(0, 0, 0, 1, oval, 1), faceCamera()), {
    sides: 8, flat: true, phase: half, twist: [0, 0, 0, half, half],
  });
  mb.blob(0.035, 0.035, 0.02, p.color('paper.0'), chain(at, place(-0.06, 0.07, 0.12)), { detail: 1 });
}

export const TREASURE: Record<string, Builder> = {
  'purple gem': (mb, p) => gem(mb, p, 'gemPurple'),
  'red gem': (mb, p) => gem(mb, p, 'gemRed'),
  'yellow diamond': (mb, p) => diamond(mb, p, 'diamondYellow'),
  'green diamond': (mb, p) => diamond(mb, p, 'diamondGreen'),
  'purple diamond': (mb, p) => diamond(mb, p, 'diamondPurple'),
  // Not a food: the blue box's giant bonus (02 §7.6), drawn 2.6x.
  'blue diamond': (mb, p) => diamond(mb, p, 'diamondBlue'),
  'yellow queen': (mb, p) => crown(mb, p, 'gold', 'gemRed'),
  'red queen': (mb, p) => crown(mb, p, 'velvetRed', 'gemGreen'),
  'green queen': (mb, p) => crown(mb, p, 'velvetGreen', 'gemRed'),
  'yellow shard': (mb, p) => {
    // A cluster of citrine crystals growing out of a lump of rock.
    mb.blob(0.44, 0.18, 0.3, p.color('rock.1'), place(0, 0.03, 0), {
      detail: 2, flatBottom: true, topColor: p.color('rock.2'),
    });
    crystal(mb, p, rotZ(0, 0.08, 0), 0.17, 0.56);
    crystal(mb, p, rotZ(-0.22, 0.06, 0.55, 0.04), 0.13, 0.36);
    crystal(mb, p, rotZ(0.24, 0.06, -0.5, 0.02), 0.13, 0.4);
    crystal(mb, p, rotZ(0.08, 0.06, -0.2, 0.18), 0.09, 0.22);
    crystal(mb, p, rotZ(-0.12, 0.05, 0.3, -0.16), 0.1, 0.3);
    mb.blob(0.04, 0.07, 0.02, p.color('paper.0'), place(-0.07, 0.58, 0.16), { detail: 1 });
  },
  'green locket': (mb, p) => locket(mb, p, 'gemGreen', false),
  'red locket': (mb, p) => locket(mb, p, 'gemRed', true),
};

/** The fallback for a name with no model: a gem in `ramp`. */
export function fallbackGem(mb: MeshBuilder, p: Palette, ramp: string): void {
  gem(mb, p, ramp);
}
